import api from '../utils/api.js';
import { userFacingDetail } from '../utils/errorHandler.js';
import { toDiarizationState } from '../pages/ActivityDetail/diarization/diarizationAdapter.js';

/**
 * 話者分離 (SS-03) API 呼び出し集約層 (PBI-004 §2、SCR-D-001 §6.3)。
 *
 * 応答本文は diarizationAdapter.js で画面の内部の形にして返す（API の形に依る処理をそこに閉じ込めるため）。
 * 失敗の形は matchingApi.js と同じ `{ok, status, retryable, errorMessage}`。
 * **自動リトライはしない**（SCR-D-001 §6.3「自動リトライを設けない理由」。ポイントが二重に消費されうる）。
 */

/**
 * 受付が済んでいるかが分からない失敗に共通の案内（2 文目以降）。
 * 利用者にしていただくことが同じ場面に、2 通りの案内を作らない（SCR-D-001 §6.3。#1468）
 *
 * 「再試行してください」と書かない: 読み直した後は、受け付けが済んでいなければ何も出ず、再実行のボタンも無い
 * （話者分離を始める入口は音声の上書きだけ）。押せるものが無いのに促す文にしないため、見るものを名指しする（#1641）
 */
const ACCEPT_UNKNOWN_GUIDE =
    '受け付けが済んでいる場合があります。'
    + 'ページを再読み込みして、「文字起こし」タブに話者分離の処理中の表示か結果が出ているかを確認してください。';

/** 話者分離の失敗の文言 (SCR-D-001 §6.3) */
export const DIARIZATION_ERROR_MESSAGES = {
    loginRequired: 'ログインが必要です。',
    /**
     * 話者分離処理失敗（受付の後の失敗で failure.message が無い場合）。SCR-D-001 §6.3 の区分の表の `stt_failed` と同じ文。
     * 「時間をおいて」を含めない: 再実行できない失敗にも出るため（再実行できるときはボタンが出ていること自体が伝える。#1643）
     */
    failed: '話者分離に失敗しました。',
    /**
     * 受付の失敗（4xx）で、サーバが文言（detail）を返さなかった場合 (SCR-D-001 §6.3「話者分離の受付の失敗」。#1468・#889)。
     * 受付の後の失敗（failed）とは分ける: 受付の失敗は「始まらなかった」ことで、処理が走って失敗したのではない。
     * 4xx は再実行を出さないため、「もう一度お試しください」と促さない
     */
    executeFailed: '話者分離を開始できませんでした。',
    /**
     * 受付の失敗（5xx）で、サーバが文言（detail）を返さなかった場合 (SCR-D-001 §6.3「話者分離の受付の失敗」。#1468)。
     * サーバが受付の記録を済ませた後に応答を返せず終わる経路がある（要求の時間切れ・インスタンスの停止）ため、
     * 「開始できませんでした」と断定しない。通信は切れていないため、通信の失敗の文（network）とも分ける
     */
    executeUnconfirmed: `話者分離を開始できたかを確認できませんでした。${ACCEPT_UNKNOWN_GUIDE}`,
    /**
     * 通信の失敗 (SCR-D-001 §6.3「話者分離の受付の失敗」。#1641)。
     * 受付が済んでいる場合があるため「届いていない」とだけ書かない（SCR-M-001 §6.3 No.2 と同じ考え方）。
     * 2 文目以降は SCR-M-001 の文と揃えない: マッチングは読み直した後も実行のボタンがあり、話者分離には無い
     */
    network: `通信が途中で終了しました。${ACCEPT_UNKNOWN_GUIDE}`,
    /**
     * 想定外の `status`（画面が解釈できない応答。失敗の区分ではなく画面とサーバの食い違い）。
     * 利用者にできるのは再読み込みのため、再実行を促さない（再実行ボタンも出さない）。fetchFailed とは別:
     * あちらは取得の通信の失敗で、次の取り直しで回復する。
     * 【仮定: 想定外の status の文言（SCR-D-001 §6.3 に定めが無い。PM 決定 2026-09-27） / 確認先=SCR-D-001 §6.3 の区分ごとの是正（DESIGN） / 追跡=#889】
     */
    unknownStatus: '話者分離の状態を取得できませんでした。ページを再読み込みしてください。',
    /** 状態の取得の失敗（画面は落とさず、次の取り直しで回復する） */
    fetchFailed: '話者分離の状態を取得できませんでした。',
};

/** 5xx（サーバ側の失敗）の状態コードの下限 */
const SERVER_ERROR_STATUS_MIN = 500;

/** 再実行を出すときに添える文言 (SCR-D-001 §6.3「再実行に消費の注意を添える理由」。SCR-M-001 §6.3 No.5 と同一) */
export const RETRY_POINTS_NOTICE = '再試行すると AI ポイントを消費する場合があります。';

/**
 * 手で編集した内容が置き換わることを知らせる文言 (SCR-D-001 §6.2「手で編集した内容が置き換わることの明示」。#1755)。
 *
 * 話者分離の結果が出ると、文字起こしの本文は結果で置き換わる（PBI-004 §9.2）。置き換える動作は変えず、そうなることを出す。
 * **文は 1 つで、出す場所（確認モーダル・再実行のボタンの近く・処理中の表示）がどこでも同じ**（同節の定め）。
 * 場所ごとに書かず、この定数を参照する（1 か所だけ変わると、設計書の文との完全一致の照合が崩れる）
 */
export const HAND_EDIT_REPLACED_NOTICE =
    '話者分離の結果が出ると、文字起こしの本文は話者分離の結果に置き換わります。それまでに手で編集した内容は残りません。';

/**
 * 失敗の応答を `{ok:false, status, retryable, errorMessage}` にする。
 * 4xx はサーバが受け付けないと判断しており、やり直しても同じ結果になるため再実行を出さない。
 * 5xx は一時的な失敗のことがあるため手動の再実行を許す（SCR-D-001 §6.3「API 通信エラー」）。
 */
async function toFailureResult(response, fallbackMessage) {
    const errData = await response.json().catch(() => ({}));
    return {
        ok: false,
        status: response.status,
        retryable: response.status >= SERVER_ERROR_STATUS_MIN,
        errorMessage: userFacingDetail(response.status, errData) || fallbackMessage,
    };
}

/**
 * 話者分離の状態と結果を取得する (`GET /api/diarization/{meeting_id}`)。
 *
 * **フラグが無効のときも 404 が返る**（PBI-004 §2.1・§8）が、画面はフラグを `/auth/me` で見て、
 * 無効ならこの関数を呼ばない（SCR-D-001 §6.1）。
 *
 * @param {string} meetingId
 * @returns {Promise<{ok: boolean, status: number, retryable?: boolean, state?: object, errorMessage?: string}>}
 */
export async function fetchDiarization(meetingId) {
    let response;
    try {
        response = await api.fetch(`/api/diarization/${encodeURIComponent(meetingId)}`);
    } catch {
        return { ok: false, status: 0, retryable: true, errorMessage: DIARIZATION_ERROR_MESSAGES.fetchFailed };
    }
    if (!response) {
        return { ok: false, status: 0, retryable: false, errorMessage: DIARIZATION_ERROR_MESSAGES.loginRequired };
    }
    if (!response.ok) {
        return toFailureResult(response, DIARIZATION_ERROR_MESSAGES.fetchFailed);
    }
    const body = await response.json();
    return { ok: true, status: response.status, state: toDiarizationState(body) };
}

/**
 * 面談の音声の応答を取得する (`GET /meetings/{meeting_id}/audio`)。
 *
 * 話者分離の必要なポイントと、`DIARIZATION` のキーがあるか（サーバがいま受付を受け付けるか）を読むために使う
 * (SCR-D-001 §5.2.1。Issue #1664)。サーバは話者分離の状態でキーを載せるかを変えるため、
 * 受付の応答の後と、結果が出たときに取り直す。
 *
 * 失敗の文言は返さない: 取得の失敗では表示を変えず、前に取れた値を残す（呼び出し側）。
 *
 * @param {string} meetingId
 * @returns {Promise<{ok: boolean, status: number, attachment?: (Object|null)}>}
 *   attachment は応答の `attachment`（音声が無ければ null）
 */
export async function fetchMeetingAudio(meetingId) {
    let response;
    try {
        response = await api.fetch(`/meetings/${encodeURIComponent(meetingId)}/audio`);
    } catch {
        return { ok: false, status: 0 };
    }
    if (!response || !response.ok) {
        return { ok: false, status: response ? response.status : 0 };
    }
    const body = await response.json();
    return { ok: true, status: response.status, attachment: body?.attachment ?? null };
}

/**
 * 話者分離の実行を受け付けさせる (`POST /api/diarization/execute`。すぐ 202 が返る)。
 *
 * 失敗（PBI-004 §2.1）: 400 ポイント不足・音声が無い、403 AI 利用権なし、404 面談が無い、
 * 409 処理中・完了済み、502 音声認識を始められない。**`POST` の失敗ではポイントを消費しない**（同表の注記）。
 *
 * @param {string} meetingId
 * @returns {Promise<{ok: boolean, status: number, retryable?: boolean, state?: object, errorMessage?: string}>}
 */
export async function executeDiarization(meetingId) {
    let response;
    try {
        response = await api.fetch('/api/diarization/execute', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ meeting_id: meetingId }),
        });
    } catch {
        return { ok: false, status: 0, retryable: true, errorMessage: DIARIZATION_ERROR_MESSAGES.network };
    }
    if (!response) {
        return { ok: false, status: 0, retryable: false, errorMessage: DIARIZATION_ERROR_MESSAGES.loginRequired };
    }
    if (!response.ok) {
        // 文言の無い 5xx は受付が済んでいることがあるため、4xx（受付が済んでいない）と既定の文を分ける
        const fallbackMessage = response.status >= SERVER_ERROR_STATUS_MIN
            ? DIARIZATION_ERROR_MESSAGES.executeUnconfirmed
            : DIARIZATION_ERROR_MESSAGES.executeFailed;
        return toFailureResult(response, fallbackMessage);
    }
    const body = await response.json();
    return { ok: true, status: response.status, state: toDiarizationState(body) };
}
